import { FormProvider, SubmitHandler, UseFormReturn } from "react-hook-form";
import { useValidators } from "../../../helpers/validation";
import { SelectItem } from "../../../components/select/Select";
import { handleFormServerErrors } from "../../../helpers/errorHandling";
import { useCustomTranslation } from "../../../helpers/translateHelpers";
import LabeledFormControl from "../../../components/forms/LabeledFormControl";
import {
  FormError,
  TextInput,
  SelectInput,
  TextAreaInput,
  AsyncSelectInput,
  UploadInput,
  useAuth,
} from "../../../components";
import SubmitButton from "../../../components/forms/SubmitButton";
import {
  CreateRequestFormValues,
  CUSTOM_ITEM_ID,
  RequestCreateSubFormType,
} from "./RequestCreateFormWrapper";
import { useState } from "react";
import { useAddRequestMutation } from "../queries";
import {
  CPRequestToAdd,
  CustomerPortalRequestType,
} from "../../../api/models/unified";
import { useNavigate } from "../../../helpers/navigationHelpers";
import { MAIN_API_ROOT } from "../../../app_config";
import { loadServiceObjectsForSelect } from "../../../helpers/serviceObjectHelper";
import { defaultSelectQuery } from "../../../helpers/tableHelpers";
import RequestOrderFields from "./RequestOrderFields";
import RequestOwnerOrderFields from "./RequestOwnerOrderFields";
import { showToast } from "../../../helpers/toastHelpers";

interface RequestCreateFormProps {
  methods: UseFormReturn<CreateRequestFormValues, unknown, undefined>;
  requestTypesArray: SelectItem[];
  setVisibleSubForm: (type: RequestCreateSubFormType) => void;
  customAddressItem?: SelectItem;
  customContactPerson?: SelectItem;
  hideServiceObjectInput?: boolean;
  hideCustomerInput?: boolean;
}

const RequestCreateForm = (props: RequestCreateFormProps) => {
  const { tError, tRequest, t } = useCustomTranslation();
  const { user } = useAuth();

  const validators = useValidators();
  const addRequestMutation = useAddRequestMutation();
  const navigate = useNavigate();

  const [isAttachmentsUploading, setIsAttachmentsUploading] = useState(false);

  const onSubmit: SubmitHandler<CreateRequestFormValues> = async (data) => {
    try {
      const dataToSubmit: CPRequestToAdd = {
        attachmentIds: data.attachments,
        message: data.message,
        title: data.title,
        type: data.type.value as CustomerPortalRequestType,
        addressId:
          data.address?.value !== CUSTOM_ITEM_ID
            ? data.address?.value
            : undefined,
        city: data.city,
        street: data.street,
        country: data.country,
        postalCode: data.postalCode,
        coordinates: data.coordinates,
        categoryId: data.category?.value,
        contactPersonId:
          data.contactPerson?.value !== CUSTOM_ITEM_ID
            ? data.contactPerson?.value
            : undefined,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone?.number ? data.phone : undefined,
        position: data.position,
        requestedDate: data.requestedDate,
        serviceObjectIds: Array.isArray(data.serviceObjects)
          ? data.serviceObjects?.map((x) => x.value)
          : data.serviceObjects
            ? [data.serviceObjects.value]
            : [],
      };

      await addRequestMutation.mutateAsync(dataToSubmit);
      await navigate.goTo(`requests`);
      showToast(tRequest("created_successfully"), "success");
    } catch (e) {
      handleFormServerErrors(e, tError, props.methods.setError);
    }
  };

  const { handleSubmit, watch } = props.methods;

  const selectedType = watch("type");

  return (
    <FormProvider {...props.methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-2 gap-4">
          <LabeledFormControl label={tRequest("type")} isRequired>
            <SelectInput
              name="type"
              items={props.requestTypesArray}
              placeholder={tRequest("type_placeholder")}
              required
              options={{ required: validators.required }}
            />
          </LabeledFormControl>
          <LabeledFormControl label={tRequest("name")} isRequired>
            <TextInput
              name="title"
              placeholder={tRequest("name_placeholder")}
              required
              options={{
                required: validators.required,
                maxLength: validators.maxLength(100),
              }}
            />
          </LabeledFormControl>
          {selectedType?.value === "OrderRequest" ? (
            <RequestOrderFields
              setVisibleSubForm={props.setVisibleSubForm}
              customAddressItem={props.customAddressItem}
              customContactPerson={props.customContactPerson}
              hideServiceObjectInput={props.hideServiceObjectInput}
            />
          ) : selectedType?.value === "OrderRequestFromOwner" ? (
            <RequestOwnerOrderFields
              setVisibleSubForm={props.setVisibleSubForm}
              customAddressItem={props.customAddressItem}
              customContactPerson={props.customContactPerson}
              hideServiceObjectInput={props.hideServiceObjectInput}
              hideCustomerInput={props.hideCustomerInput}
            />
          ) : selectedType?.value === "ServiceObjectUpdate" &&
            !props.hideServiceObjectInput ? (
            <LabeledFormControl isRequired label={tRequest("service_object")}>
              <AsyncSelectInput
                name="serviceObjects"
                placeholder={tRequest("service_object_placeholder")}
                // loadOptions={loadServiceObjects}
                loadOptions={(phrase, callback) =>
                  loadServiceObjectsForSelect(
                    {
                      ...defaultSelectQuery,
                      customerId:
                        selectedType?.value === "OrderRequest"
                          ? user?.customerId
                          : undefined,
                      searchPhrase: phrase,
                    },
                    callback,
                  )
                }
                required
                options={{ required: validators.required }}
                // Currently we do not support to create a service object inside request
                // newOption={{
                //   label: tInput("select_new_service_object"),
                //   onClick: () =>
                //     props.setVisibleSubForm("ServiceObjectSubForm"),
                // }}
              />
            </LabeledFormControl>
          ) : null}
          {selectedType && (
            <div className="col-span-2">
              <LabeledFormControl isRequired label={tRequest("description")}>
                <TextAreaInput
                  name="message"
                  placeholder={tRequest(
                    `type_${selectedType?.value}_description_placeholder`,
                  )}
                  required
                  options={{ required: validators.required }}
                />
              </LabeledFormControl>
            </div>
          )}
        </div>
        <UploadInput
          multiple
          name="attachments"
          uploadPath={`${MAIN_API_ROOT}/customerPortal/request/v2/attachment`}
          onUploadingChange={setIsAttachmentsUploading}
        />
        <FormError />
        <div className="mt-6 flex flex-col sm:flex-row sm:justify-end">
          <SubmitButton
            label={t("send")}
            icon="arrow-right"
            isDisabled={isAttachmentsUploading}
          />
        </div>
      </form>
    </FormProvider>
  );
};

export default RequestCreateForm;
